import { ApolloCache, useApolloClient, useMutation, useQuery } from "@apollo/client";

import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { useParams } from "@/router";

import NonMemberEmploymentFormModal, {
  type NonMemberEmploymentFormData,
  OTHER_JOB_TITLE_LABEL
} from "./NonMemberEmploymentFormModal";
import { UPDATE_NON_MEMBER_EMPLOYMENT_MUTATION } from "../../person/mutations";
import {
  EMPLOYMENT_FRAGMENT,
  EmploymentRow,
  GET_EMPLOYMENT_DETAILS_QUERY,
  GET_JOB_TITLES_QUERY,
  JobTitle
} from "../../person/queries";

interface EditNonMemberEmploymentModalProps {
  employmentId: string;
  personId: string;
  onClose(): void;
}

function loadEmployment(cache: ApolloCache<object>, id: string) {
  return cache.readFragment<EmploymentRow>({
    fragment: EMPLOYMENT_FRAGMENT,
    fragmentName: "EmploymentFragment",
    id: cache.identify({ __typename: "BackofficeEmployment", id })
  });
}

function toProps(employment: EmploymentRow, jobTitles: JobTitle[]) {
  let availableJobTitles = jobTitles;

  let jobTitle = jobTitles.find(({ jobTitle: title }) => title === employment.title);
  let customJobTitle = "";

  if (!jobTitle && employment.title) {
    const otherJobTitle = jobTitles.find(({ jobTitle: title }) => title === OTHER_JOB_TITLE_LABEL);

    if (otherJobTitle) {
      // A saved title that is not a standard available title was entered via "Other",
      // so preselect "Other" and load the saved value into the free-text field.
      jobTitle = otherJobTitle;
      customJobTitle = employment.title;
    } else {
      // Surface the current title even if it is no longer in the approved list so the
      // edit form can preselect it instead of clearing the field.
      jobTitle = {
        id: "unapproved-job-title-id",
        jobTitle: employment.title,
        jobClassification: employment.jobClassification ?? ""
      };
      availableJobTitles = [jobTitle, ...jobTitles];
    }
  }

  return {
    values: {
      facilityId: employment.facility.id,
      jobTitleId: jobTitle?.id ?? "",
      customJobTitle,
      startDate: employment.startedOn ?? "",
      endDate: employment.endedOn ?? "",
      note: ""
    },
    availableJobTitles
  };
}

export default function EditNonMemberEmploymentModal({
  employmentId,
  personId,
  onClose
}: EditNonMemberEmploymentModalProps) {
  const { pgaId } = useParams("/people/:pgaId/career/employment");
  const { cache } = useApolloClient();
  const employment = loadEmployment(cache, employmentId);

  const { data: jobTitlesData } = useQuery(GET_JOB_TITLES_QUERY, {
    variables: { personId }
  });

  const [updateNonMemberEmployment] = useMutation(UPDATE_NON_MEMBER_EMPLOYMENT_MUTATION, {
    refetchQueries: [
      { query: GET_EMPLOYMENT_DETAILS_QUERY, variables: { pgaId } },
      { query: PERSON_DETAILS_HEADER_QUERY, variables: { pgaId } }
    ],
    awaitRefetchQueries: true
  });

  if (!employment) return null;

  const onSubmit = async ({ facilityId, jobTitle, startDate, endDate, note }: NonMemberEmploymentFormData) => {
    const trimmedNote = note?.trim();

    const { data } = await updateNonMemberEmployment({
      variables: {
        input: {
          id: employmentId,
          facilityId,
          jobTitle,
          startedOn: startDate || null,
          endedOn: endDate || null,
          note: trimmedNote ? { content: trimmedNote, priority: "NONE", title: "Edit Employment" } : undefined
        }
      }
    });

    return data?.updateNonMemberEmployment || { success: false, message: "Failed to update employment" };
  };

  const { values, availableJobTitles } = toProps(employment, jobTitlesData?.person.availableJobTitles || []);

  return (
    <NonMemberEmploymentFormModal
      open
      headline="Edit Employment"
      jobTitles={availableJobTitles}
      values={values}
      initialFacilityName={employment.facility.name}
      onSubmit={onSubmit}
      onClose={onClose}
    />
  );
}
